import { Request } from 'express';
import {
  filter,
  first,
  fromPairs,
  includes,
  map,
  replace,
  some,
  split,
  startsWith,
  trim,
} from 'es-toolkit/compat';
import { matchPath } from 'react-router-dom';
import { parse, stringify } from '@tock/query-string';

// Every route patterns should be in this variable
export const wwwRoutes = fromPairs(
  map(
    [
      '/__time',
      '/account',
      '/activate',
      '/affiliate-terms',
      '/app',
      '/book-by-text',
      '/collections',
      '/cookie-policy',
      '/c',
      '/chase-pay-terms',
      '/sapphire-events-terms',
      '/city',
      '/concierge-terms',
      '/device',
      '/experimental',
      '/forgot-password',
      '/gift',
      '/group',
      '/help',
      '/login',
      '/logout',
      '/merchant-dpa',
      '/merchant-terms',
      '/oauth-check',
      '/oauth-redir',
      '/order',
      '/pay',
      '/playlist',
      '/privacy',
      '/privacy-specific',
      '/profile',
      '/rates',
      '/reset',
      '/search',
      '/shop',
      '/signup',
      '/sweepstakes-rules',
      '/terms',
      '/gift-card-terms',
      '/togo',
      '/widget-builder',
      '/widget-plugin',
      '/iframe-redir',
      '/walkin-waitlist',
      '/wineries',
    ],
    (entry) => [entry, true]
  )
);

const baseConfig = Object.keys(wwwRoutes).map((key) => ({
  path: key,
}));

export const wwwRoutesConfig = baseConfig;

export const disallowedChaseUrRoutePrefixes = [
  '/app',
  '/concierge',
  '/concierge-terms',
  '/chase-pay-terms',
  '/sapphire-events-terms',
  '/device',
  '/oauth-check',
  '/profile',
  '/widget-builder',
  '/widget-plugin',
  '/privacy',
  '/terms',
  '/gift-card-terms',
  '/sweepstakes-rules',
  '/gift',
];

export const allowedChaseUrRoutes = [
  '/profile/login',
  '/profile/signup',
  '/profile/forgot-password',
  '/profile/reset',
  '/profile/reservations',
  '/profile/reservations/signed-out',
  '/profile/signed-out',
  '/profile/account/signed-out',
];

export function getDomain(pathname: string) {
  let domain;
  const path = first(split(pathname, '?')) || '';
  domain = first(filter(split(path, '/'))) || 'www';
  if (wwwRoutes['/' + domain]) {
    domain = 'www';
  }
  return domain;
}

export function isLocalRequest(req?: Request) {
  if (!req) {
    return false;
  }
  return isLocalHostname(req.headers.host || req.hostname);
}

export function isLocalHostname(hostname: string) {
  return (
    includes(hostname, 'localhost') ||
    includes(hostname, 'host.docker.internal') ||
    startsWith(hostname, '127.') ||
    startsWith(hostname, '192.') ||
    startsWith(hostname, '10.') ||
    startsWith(hostname, '100.') ||
    includes(hostname, '.local') ||
    includes(hostname, '.ngrok-free.app')
  );
}

export function isExternalNonTockUrl(href: string) {
  return includes(href, '://') && !includes(href, 'exploretock.com') && !isLocalHostname(href);
}

export function isChaseUrl(pathname: string) {
  return includes(pathname, 'chase.com');
}

export function isTockDomainSpecified(href: string) {
  const { query } = parseUrl(href);
  return (includes(href, 'exploretock.com') || isLocalHostname(href)) && !query['originToken'];
}

export function isDisallowedForChaseUR(href: string): boolean {
  const { pathname } = parseUrl(href);
  const path = replace(pathname, /https?:\/\/[^/]*/, '');
  return (
    some(disallowedChaseUrRoutePrefixes, (prefix) => startsWith(path, prefix)) &&
    !some(allowedChaseUrRoutes, (route) => path === route)
  );
}

export interface ParsedUrl {
  pathname: string;
  query: Dictionary<string>;
}

export function parseUrl(url: string): ParsedUrl {
  const pathname = first(split(url, '?')) || '';
  const queryString = url.substring(Math.min(pathname.length + 1, url.length));
  const query = parse(queryString);
  return {
    pathname,
    query,
  };
}

export function urlWithQuery(url: string, query: Dictionary<string>) {
  const params = stringify(query);
  return trim(url) + (params ? '?' + stringify(query) : '');
}

export function pathNameFromString(href: string) {
  return includes(href, '?') ? href.split('?')[0] : href;
}

export function inAvailabilitySearch(location: string) {
  return matchPath(pathNameFromString(location), ['/togo', '/city', '/group']);
}
